const SECTION_TAGS = new Set(['section', 'header', 'footer', 'article', 'aside', 'main', 'nav', 'div']);
const PREAMBLE_TAGS = new Set(['link', 'style', 'meta', 'script']);

const generateId = () => Math.random().toString(36).slice(2, 9);

export const isSectionElement = (el) => {
  if (!el || el.nodeType !== 1) return false;
  const tag = el.tagName.toLowerCase();
  if (!SECTION_TAGS.has(tag)) return false;
  if (el.classList?.contains('widget-section')) return true;
  if (el.classList?.contains('hero-section') || el.classList?.contains('widget-hero')) return true;
  if (el.hasAttribute('data-section-name') || el.hasAttribute('data-section-id')) return true;
  return tag !== 'div';
};

export const resolveSectionRoots = (nodes) => {
  const list = Array.from(nodes || []);
  const direct = list.filter(isSectionElement);
  if (direct.length >= 2) return { roots: direct, wrapperClass: null, prefix: '' };

  let prefix = '';
  const contentNodes = [];

  list.forEach((node) => {
    const tag = node.tagName.toLowerCase();
    if (PREAMBLE_TAGS.has(tag)) {
      prefix += `${node.outerHTML}\n`;
      return;
    }
    contentNodes.push(node);
  });

  if (contentNodes.length === 1) {
    const wrapper = contentNodes[0];
    const tag = wrapper.tagName.toLowerCase();
    if (tag === 'div' && !wrapper.hasAttribute('data-section-id')) {
      const inner = Array.from(wrapper.children).filter(isSectionElement);
      if (inner.length > 0) {
        return {
          roots: inner,
          wrapperClass: wrapper.getAttribute('class') || null,
          prefix,
        };
      }
    }
  }

  return { roots: contentNodes.filter(isSectionElement), wrapperClass: null, prefix };
};

export const parseHtmlStructure = (html) => {
  if (!html || typeof html !== 'string') {
    return { sections: [], envelope: { prefix: '', wrapperClass: null } };
  }

  const parser = new DOMParser();
  let sourceHtml = html;
  if (/<html[\s>]/i.test(html.trim()) || /<!DOCTYPE/i.test(html.trim())) {
    const fullDoc = parser.parseFromString(html, 'text/html');
    sourceHtml = fullDoc.body?.innerHTML || html;
  }

  const doc = parser.parseFromString(`<div id="parse-root">${sourceHtml}</div>`, 'text/html');
  const container = doc.getElementById('parse-root');
  if (!container) {
    return { sections: [], envelope: { prefix: '', wrapperClass: null } };
  }

  const { roots, wrapperClass, prefix } = resolveSectionRoots(container.children);
  const sections = [];

  roots.forEach((child, index) => {
    const tagName = child.tagName.toLowerCase();
    const id = child.getAttribute('data-section-id') || `section-${generateId()}`;
    let name = child.getAttribute('data-section-name');

    if (!name) {
      const heading = child.querySelector('h1, h2, h3, h4, h5, h6');
      if (heading && heading.textContent.trim()) {
        name = heading.textContent.trim().slice(0, 60);
      } else if (child.id) {
        name = child.id;
      } else {
        const textNode = child.textContent ? child.textContent.trim() : '';
        if (textNode) {
          name = textNode.slice(0, 40) + (textNode.length > 40 ? '…' : '');
        } else {
          const label = tagName.charAt(0).toUpperCase() + tagName.slice(1);
          name = `${label} section ${index + 1}`;
        }
      }
    }

    const showInNav = child.getAttribute('data-show-nav') === 'true';
    child.setAttribute('data-section-id', id);
    child.setAttribute('id', id);
    child.setAttribute('data-section-name', name);

    sections.push({
      id,
      name,
      html: child.outerHTML,
      type: tagName,
      showInNav,
    });
  });

  return {
    sections,
    envelope: { prefix, wrapperClass },
  };
};

export const parseHtmlToSections = (html) => parseHtmlStructure(html).sections;

export const sectionsToHtml = (sections, envelope = {}) => {
  const inner = (sections || []).map((s) => s.html).join('\n');
  const prefix = envelope.prefix || '';
  const wrapperClass = envelope.wrapperClass || null;

  if (wrapperClass) {
    return `${prefix}<div class="${wrapperClass}">\n${inner}\n</div>`;
  }
  return `${prefix}${inner}`;
};
